import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'i18n-js';
import { buildUrl, formatAmount, truncateKeywordSpaces, forceTimezone, toQueryString } from 'utils';
import {
  Grid,
  Paper,
  Container,
  Button,
  Box,
  Tooltip,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Switch,
} from '@material-ui/core';
import Layout from 'components/Layout';
import Title from 'components/Title';
import { promoCodesActions } from 'state/modules/promoCodes';
import { Table } from 'components/UI';
import { Link } from 'react-router-dom';
import { useStyles, switchStyles } from './PromoCodeList.styled';
import { FETCH_STATE } from 'constants';
import { useHistory } from 'react-router-dom';
import Can from 'components/Can';
import {
  Edit as EditIcon,
  FileCopy as FileCopyIcon,
  Delete as DeleteIcon,
} from '@material-ui/icons';
import { useConfirm, useQuery } from 'hooks';
import { Search as SearchIcon } from '@material-ui/icons';
import { SearchField } from 'components/FormFields';
import { UpdatePromoCodeModal } from 'components/Modals';
import moment from 'moment';
import { debounce } from 'lodash';

const ActionsMenu = ({ row, handleUpdatePromoCodeModal }) => {
  const dispatch = useDispatch();

  const deletePromoCode = useCallback(
    (id) => {
      dispatch(promoCodesActions.deletePromoCode(id));
    },
    [dispatch]
  );

  const [toggleDeletePromoCode, DeletePromoCode] = useConfirm({
    title: i18n.t('promo_codes.delete'),
    message: i18n.t('promo_codes.delete_description'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.delete'),
    onConfirm: () => deletePromoCode(row.id),
  });

  const handleDelete = () => {
    toggleDeletePromoCode();
  };

  return (
    <Box display="flex" justifyContent="center">
      <Can I="update" a="promotional_code">
        <Tooltip title={i18n.t('promo_codes.edit')}>
          <IconButton
            variant="contained"
            aria-haspopup="true"
            aria-controls="card-menu"
            aria-label={i18n.t('promo_codes.edit')}
            size="medium"
            onClick={() => {
              handleUpdatePromoCodeModal(row);
            }}
            data-cy={`promo-codes-edit-${row?.code}`}
          >
            <EditIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Can>
      <Can I="create" a="promotional_code">
        <Tooltip title={i18n.t('promo_codes.clone')}>
          <IconButton
            variant="contained"
            aria-haspopup="true"
            aria-controls="card-menu"
            aria-label={i18n.t('promo_codes.clone')}
            size="medium"
            component={Link}
            to={buildUrl(`/promo-codes/${row?.id}/clone`)}
            data-cy={`promo-codes-clone-${row?.code}`}
          >
            <FileCopyIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Can>
      <Can I="delete" on="promotional_code">
        <Tooltip title={i18n.t('promo_codes.delete')}>
          <IconButton
            variant="contained"
            aria-haspopup="true"
            aria-controls="card-menu"
            aria-label={i18n.t('promo_codes.delete')}
            onClick={handleDelete}
            data-cy={`promo-codes-delete-${row?.code}`}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Can>
      <DeletePromoCode />
    </Box>
  );
};

const PromoCodeList = () => {
  const urlParams = useQuery();
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    ...urlParams,
    is_active: urlParams.is_active === 'true' ? true : undefined,
  });
  const [showUpdatePromoCodeModal, toggleShowUpdatePromoCodeModal] = useState(false);
  const [currentPromoCodeId, setCurrentPromoCodeId] = useState(null);
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();
  const switchClasses = switchStyles();
  const isMounted = useRef(false);
  const abortController = useRef(null);

  // We use multiple selectors to avoid table rerender / consider using reselect.
  const data = useSelector((state) => state.promoCodes.promoCodes);
  const pagination = useSelector((state) => state.promoCodes.pagination);
  const fetchingPromoCodes = useSelector((state) => state.promoCodes.fetchingPromoCodes);
  const deletePromoCode = useSelector((state) => state.promoCodes.deletePromoCode);
  const updatePromoCode = useSelector((state) => state.promoCodes.updatePromoCode);
  const localeFormat = useSelector((state) => state.organizations.localeFormat);

  const refetch = deletePromoCode === FETCH_STATE.DONE || updatePromoCode === FETCH_STATE.DONE;
  const loading = fetchingPromoCodes === FETCH_STATE.LOADING;

  const fetchPromoCodes = useCallback(
    (params = {}) => {
      // Abort previous if any request
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      dispatch(
        promoCodesActions.fetchPromoCodes(params, { signal: abortController?.current?.signal })
      );
    },
    [dispatch]
  );

  const fetchPromoCodesDebounced = useCallback(
    debounce((params = {}) => {
      fetchPromoCodes(params);
    }, 1000),
    [dispatch]
  );

  // Fetch on params change
  useEffect(() => {
    if (isMounted.current) {
      fetchPromoCodesDebounced(params);
    } else {
      isMounted.current = true;
    }
  }, [fetchPromoCodes, params]);

  // Run once on mount, no debounce
  useEffect(() => {
    fetchPromoCodes({ ...params });
  }, []);

  // Fetch when data is outdated
  useEffect(() => {
    if (refetch) {
      fetchPromoCodes(params);
    }
  }, [fetchPromoCodes, params, refetch]);

  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    const qs = toQueryString(newParams);
    history.replace(buildUrl(`/promo-codes${qs}`));
    setParams(newParams);
  };

  const handleSearch = (name, value) => {
    const searchParams = {
      ...params,
      page: 1,
      [name]: truncateKeywordSpaces(value),
    };
    const qs = toQueryString(searchParams);
    history.replace(buildUrl(`/promo-codes${qs}`));
    setParams(searchParams);
  };

  const handlePageChange = (page) => {
    const newParams = { ...params, page };
    const qs = toQueryString(newParams);
    history.replace(buildUrl(`/promo-codes${qs}`));
    setParams(newParams);
  };

  const handlePerRowsChange = async (limit) => {
    const newParams = { ...params, page: 1, limit };
    const qs = toQueryString(newParams);
    history.replace(buildUrl(`/promo-codes${qs}`));
    setParams(newParams);
  };

  const handleToggle = (event) => {
    const is_active = event.target.checked;
    const newParams = { ...params, is_active };
    //Remove is_active from query string if it is false to avoid so filter does
    // not show only non active promo codes
    if (!is_active) delete newParams.is_active;
    const qs = toQueryString(newParams);
    history.replace(buildUrl(`/promo-codes${qs}`));
    setParams(newParams);
  };

  /**
   * Handle row click
   * @param {object} row Data of the row selected
   */
  const handleRowClick = (row) => {
    history.push(buildUrl(`/promo-codes/${row?.id}`));
  };

  const handleUpdatePromoCodeModal = useCallback((row) => {
    setCurrentPromoCodeId((state) => (state ? null : row?.id));
    toggleShowUpdatePromoCodeModal((state) => !state);
  });

  const columns = [
    {
      name: i18n.t('promo_codes.id'),
      selector: 'code',
      grow: 1,
      minWidth: '9rem',
      sortable: true,
    },
    {
      name: i18n.t('common.description'),
      selector: 'description',
      format: (row) => row.description || '--',
      grow: 2,
    },
    {
      name: i18n.t('promo_codes.credit_amount'),
      selector: 'amount',
      format: (row) => `$${formatAmount(row.amount || 0)}`,
      grow: 0,
      minWidth: '9rem',
      sortable: true,
    },
    {
      name: i18n.t('promo_codes.redemptions'),
      selector: 'total_emitted',
      format: (row) => row.total_emitted || 0,
      grow: 0,
      minWidth: '10rem',
      sortable: false,
    },
    {
      name: i18n.t('promo_codes.start_time'),
      selector: 'start_time',
      format: (row) =>
        row?.start_time ? forceTimezone(moment(row?.start_time)).format(`${localeFormat}`) : '--',
      grow: 0,
      minWidth: '8.5rem',
      sortable: true,
    },
    {
      name: i18n.t('promo_codes.expiration_time'),
      selector: 'expiration_time',
      format: (row) =>
        row?.expiration_time
          ? forceTimezone(moment(row?.expiration_time))
              .subtract(1, 'days')
              .format(`${localeFormat}`)
          : '--',
      grow: 0,
      minWidth: '8.5rem',
      sortable: true,
    },
    {
      name: i18n.t('common.enabled'),
      selector: 'is_enabled',
      format: (row) => (row.is_enabled ? i18n.t('common.yes') : i18n.t('common.no')),
      grow: 0,
      minWidth: '5rem',
      sortable: true,
    },
    {
      grow: 0,
      center: true,
      name: i18n.t('common.actions'),
      cell: (row) => (
        <ActionsMenu row={row} handleUpdatePromoCodeModal={handleUpdatePromoCodeModal} />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      sortable: false,
      minWidth: '9rem',
    },
  ];

  return (
    <Layout title={i18n.t('promo_codes.title')}>
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2} direction="row" alignItems="center" justifyContent="space-between">
              <Grid item>
                <Title title={i18n.t('promo_codes.title')} />
              </Grid>
              <Grid item>
                <Can I="create" a="promotional_code">
                  <Button
                    component={Link}
                    to={buildUrl('/promo-codes/create')}
                    style={{ textTransform: 'capitalize' }}
                    variant="contained"
                    color="primary"
                    data-cy="create-promo-code-btn"
                  >
                    {i18n.t('promo_codes.create')}
                  </Button>
                </Can>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2} direction="row" alignItems="flex-end">
              <Grid item xs={4}>
                <SearchField
                  margin="none"
                  label={i18n.t('promo_codes.search')}
                  placeholder={i18n.t('promo_codes.search_placeholder')}
                  field={{ onChange: handleSearch, name: 'keyword', value: params.keyword }}
                  inputProps={{
                    startAdornment: (
                      <InputAdornment
                        classes={{
                          root: classes.searchIcon,
                        }}
                        position="start"
                      >
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  data-cy="search-promo-code-input"
                />
              </Grid>
              <FormControlLabel
                value="start"
                classes={switchClasses}
                control={
                  <Switch
                    checked={params?.is_active}
                    onChange={handleToggle}
                    color="primary"
                    inputProps={{ 'aria-label': i18n.t('promo_codes.is_active') }}
                  />
                }
                label={i18n.t('promo_codes.is_active')}
                labelPlacement="start"
              />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Paper elevation={2} className={classes.paper}>
              <Table
                data={data}
                columns={columns}
                onSort={handleSort}
                loading={loading}
                handleRowClicked={handleRowClick}
                handlePageChange={handlePageChange}
                totalRows={pagination?.totalRows || 0}
                handlePerRowsChange={handlePerRowsChange}
                page={params?.page}
                limit={params?.limit}
                defaultSortField={params?.sort_field}
                defaultSortAsc={!params?.sort_desc}
              />
            </Paper>
          </Grid>
        </Grid>
      </Container>
      {showUpdatePromoCodeModal && currentPromoCodeId && (
        <UpdatePromoCodeModal
          open={showUpdatePromoCodeModal}
          id={currentPromoCodeId}
          handleCancel={handleUpdatePromoCodeModal}
        />
      )}
    </Layout>
  );
};

export default PromoCodeList;
